import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogFooterProps,
  DialogHeader,
  DialogTitle
} from "@cmp-src/primitives/Dialog/Dialog";
import { cn } from "@gettactic/helpers";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import React from "react";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";

export interface ModalProps extends React.PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  title?: string | React.ReactNode;
  size?: string;
  closeOnOverlayClick?: boolean;
  isModal?: boolean;
  className?: string;
  contentClassName?: string;
}

export interface ModalComponent extends React.FC<ModalProps> {
  Body: React.FC<React.PropsWithChildren>;
  Footer: React.FC<DialogFooterProps>;
}

export const Modal: ModalComponent = ({
  children,
  isOpen,
  onClose,
  title,
  size,
  closeOnOverlayClick = true,
  isModal,
  className,
  contentClassName
}) => {
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const titleContent =
    typeof title === "string" ? (
      <span className="font-bold text-2xl text-secondary-foreground">
        {title}
      </span>
    ) : (
      title || null
    );

  const content = (
    <div
      style={{
        maxHeight: browserHeight ? `${browserHeight - 200}px` : "32rem"
      }}
      className={cn(
        "pr-3 pl-1 overflow-x-hidden",
        browserHeight && browserHeight < 700 ? "overflow-y-auto" : ""
      )}
    >
      {children}
    </div>
  );

  if (!isMd) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        onOpenChange={(value) => {
          if (!value) onClose();
        }}
      >
        <div className={"flex flex-col gap-4 p-4"}>
          <div>{titleContent}</div>
          <div>{content}</div>
        </div>
      </MobileDrawer>
    );
  }

  // Check if the clicked element is part of a Radix Select portal
  const isSelectPortal = (element: Element | null): boolean => {
    if (!element) return false;

    // Check if element or any parent has data-radix-select-* attributes
    let current: Element | null = element;
    while (current) {
      const attrs = current.attributes;
      for (let i = 0; i < attrs.length; i++) {
        if (attrs[i].name.startsWith("data-radix-select")) {
          return true;
        }
      }
      current = current.parentElement;
    }
    return false;
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      modal={isModal}
    >
      <DialogContent
        className={cn(`${size ? size : "max-w-[30rem]"}`, className)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayProps={{
          onClick: closeOnOverlayClick
            ? (e: React.MouseEvent) => {
                // Only close when clicking directly on the overlay, not when
                // clicks from modal content bubble up to the overlay parent
                if (e.target === e.currentTarget) onClose();
              }
            : undefined
        }}
        onInteractOutside={(e) => {
          // When closeOnOverlayClick is false, prevent all outside interactions
          // Otherwise, only prevent closing when clicking on Select portals
          if (!closeOnOverlayClick || isSelectPortal(e.target as Element)) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          // When closeOnOverlayClick is false, prevent all outside interactions
          // Otherwise, only prevent closing when clicking on Select portals
          if (!closeOnOverlayClick || isSelectPortal(e.target as Element)) {
            e.preventDefault();
          }
        }}
      >
        {/* Radix requires a DialogTitle for accessibility — render the
            visible title through it, or a screen-reader-only fallback. */}
        <DialogHeader>
          {titleContent ? (
            <DialogTitle className="font-normal">{titleContent}</DialogTitle>
          ) : (
            <DialogTitle className="sr-only">Dialog</DialogTitle>
          )}
        </DialogHeader>
        <div
          className={cn(
            `py-4 overflow-hidden ${size ? size : "max-w-[30rem]"}`,
            contentClassName
          )}
        >
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ModalBody: ModalComponent["Body"] = ({ children }) => {
  return <>{children}</>;
};

export const ModalFooter: ModalComponent["Footer"] = (props) => {
  return (
    <DialogFooter {...props} className={cn("mt-4 mb-1", props.className)} />
  );
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
